import { isValidBdcStateAlias } from "../../components/vault/documents/id-validation/idDataValidation";
import {
  BDC_ADDRESS_TOTAL_FORMAT_ERROR,
  getBdcStreetAddressProjection as projectStreetAddress,
  normalizeBdcName,
} from "./bdcValidationShared";

export {
  BDC_ADDRESS_TOTAL_FORMAT_ERROR as BDC_COMPANY_ADDRESS_LENGTH_ERROR,
} from "./bdcValidationShared";

export const BDC_COMPANY_NAME_FORMAT_ERROR =
  "La razón social debe tener de 2 a 75 caracteres y usar sólo letras, números, '&' y espacios.";

const COMPANY_NAME_PATTERN = /^[0-9A-Z& ]+$/;
const POSTAL_CODE_PATTERN = /^\d{5}$/;

type ValidationValues = Record<string, unknown>;

function readValue(values: ValidationValues, key: string): string {
  return String(values[key] ?? "");
}

/** Mirrors sanitize_company_name_for_backend before applying the BDC contract. */
export function normalizeBdcCompanyName(value: string): string {
  return normalizeBdcName(value.replace(/[.,]/g, ""));
}

/** Mirrors sanitize_for_backend for address components sent to SIC. */
export function sanitizeBdcInput(value: string): string {
  return normalizeBdcName(value);
}

/** Returns the exact normalized street string sent to the SIC adapter. */
export function getBdcStreetAddressProjection(values: ValidationValues): string {
  return projectStreetAddress(values, [
    "street",
    "exterior_number",
    "interior_number",
  ]);
}

/** Validate PM values against the final company payload sent to Platform/SIC. */
export function validateBdcCompanyData(
  values: ValidationValues,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const legalName = normalizeBdcCompanyName(
    readValue(values, "business_legal_name"),
  );
  if (
    legalName.length < 2 ||
    legalName.length > 75 ||
    !COMPANY_NAME_PATTERN.test(legalName)
  ) {
    errors.business_legal_name = BDC_COMPANY_NAME_FORMAT_ERROR;
  }

  const addressRules: readonly [string, number, number, string][] = [
    ["street", 2, 80, "La calle debe tener de 2 a 80 caracteres."],
    [
      "exterior_number",
      1,
      10,
      "El número exterior debe tener de 1 a 10 caracteres.",
    ],
    ["neighborhood", 2, 65, "La colonia debe tener de 2 a 65 caracteres."],
    [
      "municipality",
      2,
      65,
      "El municipio o alcaldía debe tener de 2 a 65 caracteres.",
    ],
  ];
  for (const [key, min, max, message] of addressRules) {
    const value = sanitizeBdcInput(readValue(values, key));
    if (value.length < min || value.length > max) errors[key] = message;
  }

  const interiorNumber = sanitizeBdcInput(
    readValue(values, "interior_number"),
  );
  if (interiorNumber.length > 10) {
    errors.interior_number =
      "El número interior no puede exceder 10 caracteres.";
  }

  const postalCode = readValue(values, "postal_code").trim();
  if (!POSTAL_CODE_PATTERN.test(postalCode)) {
    errors.postal_code = "Ingresa un código postal de 5 dígitos.";
  }

  const state = readValue(values, "state");
  if (!isValidBdcStateAlias(state)) {
    errors.state = "Selecciona una entidad federativa válida.";
  }

  if (getBdcStreetAddressProjection(values).length > 80) {
    errors.street = BDC_ADDRESS_TOTAL_FORMAT_ERROR;
  }

  return errors;
}
